import React, { useState } from "react";
import { AlertTriangle, Check, Trash2, CheckCircle2 } from "lucide-react";

/**
 * VehicleInspectionDiagram — customer-facing pre-rental vehicle inspection.
 *
 * Shows a top-down car SVG. The customer taps anywhere on the diagram to
 * drop a marker (dent / scratch / paint / crack / other), optionally adds a
 * short note, and can remove markers with a trash icon. Coordinates are
 * stored as {x, y} in percentages so the diagram is resolution-independent
 * when re-rendered in the admin PDF.
 *
 * Parent controls `inspection` state:
 *   { markers: [{id, x_pct, y_pct, type, note, ts}], acknowledged: bool, acknowledged_at: iso }
 *
 * `acknowledged: true` is required before the parent's Next button unlocks —
 * the customer either flags every pre-existing defect OR affirmatively states
 * the vehicle looks clean.
 */
const MARKER_TYPES = [
  { key: "dent",     label: "Dent",     color: "#f97316" },
  { key: "scratch",  label: "Scratch",  color: "#fbbf24" },
  { key: "paint",    label: "Paint chip", color: "#a855f7" },
  { key: "crack",    label: "Crack",    color: "#ef4444" },
  { key: "other",    label: "Other",    color: "#38bdf8" },
];

export default function VehicleInspectionDiagram({ value, onChange, testId = "vehicle-inspection", readOnly = false }) {
  const inspection = value || { markers: [], acknowledged: false, acknowledged_at: null };
  const [pendingType, setPendingType] = useState("dent");

  const addMarker = (evt) => {
    if (readOnly) return;
    const rect = evt.currentTarget.getBoundingClientRect();
    const x_pct = ((evt.clientX - rect.left) / rect.width) * 100;
    const y_pct = ((evt.clientY - rect.top) / rect.height) * 100;
    const marker = {
      id: `m_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      x_pct: Math.round(x_pct * 10) / 10,
      y_pct: Math.round(y_pct * 10) / 10,
      type: pendingType,
      note: "",
      ts: new Date().toISOString(),
    };
    onChange({ ...inspection, markers: [...(inspection.markers || []), marker], acknowledged: false, acknowledged_at: null });
  };

  const removeMarker = (id) => {
    onChange({ ...inspection, markers: (inspection.markers || []).filter((m) => m.id !== id) });
  };

  const setNote = (id, note) => {
    onChange({ ...inspection, markers: (inspection.markers || []).map((m) => m.id === id ? { ...m, note } : m) });
  };

  const acknowledge = () => {
    onChange({ ...inspection, acknowledged: true, acknowledged_at: new Date().toISOString() });
  };

  const markerCount = (inspection.markers || []).length;

  // Read-only staff view: skip the type picker, acknowledgment button, and
  // interactive controls entirely. Just render the diagram + marker list.
  if (readOnly) {
    return (
      <div data-testid={testId} className="rounded-2xl border border-cyan/25 bg-cyan/[0.03] p-4 space-y-3">
        <div>
          <div className="label-caps text-cyan text-[10px]">Customer Inspection · {markerCount} flagged</div>
          {inspection.acknowledged_at && (
            <div className="text-[10px] text-white/50 mt-0.5">
              Acknowledged {new Date(inspection.acknowledged_at).toLocaleString()}
            </div>
          )}
        </div>
        <div className="relative rounded-xl bg-white/[0.02] border border-white/10" style={{ aspectRatio: "2 / 1" }}>
          <svg viewBox="0 0 600 300" preserveAspectRatio="xMidYMid meet" className="w-full h-full pointer-events-none">
            <rect x="80" y="30" width="440" height="240" rx="60" fill="none" stroke="#94a3b8" strokeWidth="3" />
            <path d="M 145 90 Q 145 60 190 60 L 410 60 Q 455 60 455 90 L 455 130 L 145 130 Z" fill="none" stroke="#94a3b8" strokeWidth="2" />
            <path d="M 145 210 L 455 210 L 455 240 Q 455 270 410 270 L 190 270 Q 145 270 145 240 Z" fill="none" stroke="#94a3b8" strokeWidth="2" />
            <line x1="145" y1="170" x2="455" y2="170" stroke="#475569" strokeWidth="1" strokeDasharray="4,4" />
            <rect x="70" y="65" width="18" height="45" rx="4" fill="#475569" />
            <rect x="70" y="190" width="18" height="45" rx="4" fill="#475569" />
            <rect x="512" y="65" width="18" height="45" rx="4" fill="#475569" />
            <rect x="512" y="190" width="18" height="45" rx="4" fill="#475569" />
            <text x="300" y="20" textAnchor="middle" fill="#64748b" fontSize="12">FRONT</text>
            <text x="300" y="295" textAnchor="middle" fill="#64748b" fontSize="12">REAR</text>
          </svg>
          {(inspection.markers || []).map((m, i) => {
            const color = (MARKER_TYPES.find((t) => t.key === m.type) || {}).color || "#38bdf8";
            return (
              <div key={m.id}
                className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%` }}>
                <div className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-[9px] font-bold text-white"
                     style={{ background: color }}>{i + 1}</div>
              </div>
            );
          })}
        </div>
        {markerCount > 0 && (
          <div className="space-y-1">
            {(inspection.markers || []).map((m, i) => {
              const t = MARKER_TYPES.find((x) => x.key === m.type) || MARKER_TYPES[4];
              return (
                <div key={m.id} className="flex items-center gap-2 text-[11px] text-white/80">
                  <div className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold text-white"
                       style={{ background: t.color }}>{i + 1}</div>
                  <span className="font-medium w-20">{t.label}</span>
                  <span className="flex-1 truncate">{m.note || <span className="text-white/40">no note</span>}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid={testId} className="rounded-2xl border border-cyan/25 bg-cyan/[0.03] p-4 space-y-3">
      <div>
        <div className="label-caps text-cyan text-[10px]">Vehicle Inspection · Flag Pre-Existing Damage</div>
        <div className="text-xs text-white/60 mt-0.5">
          Tap the diagram to mark any dents, scratches, or paint damage you can see. If the vehicle looks clean, just tap
          "Confirm the vehicle looks clean" below.
        </div>
      </div>

      {/* Marker type picker */}
      <div className="flex flex-wrap gap-1.5">
        {MARKER_TYPES.map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`${testId}-type-${t.key}`}
            onClick={() => setPendingType(t.key)}
            className={`px-2.5 py-1 rounded-full text-[10px] label-caps border transition inline-flex items-center gap-1 ${
              pendingType === t.key
                ? "border-cyan text-cyan bg-cyan/10"
                : "border-white/12 text-white/60 hover:text-white/90"
            }`}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: t.color }} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Top-down car SVG. Coordinates are viewBox-based so markers scale with any container size. */}
      <div
        data-testid={`${testId}-canvas`}
        onClick={addMarker}
        className="relative rounded-xl bg-white/[0.02] border border-white/10 cursor-crosshair select-none"
        style={{ aspectRatio: "2 / 1", userSelect: "none" }}
      >
        <svg viewBox="0 0 600 300" preserveAspectRatio="xMidYMid meet" className="w-full h-full pointer-events-none">
          {/* Simple top-down sedan / SUV outline */}
          <rect x="80" y="30" width="440" height="240" rx="60" fill="none" stroke="#94a3b8" strokeWidth="3" />
          {/* Windshield */}
          <path d="M 145 90 Q 145 60 190 60 L 410 60 Q 455 60 455 90 L 455 130 L 145 130 Z" fill="none" stroke="#94a3b8" strokeWidth="2" />
          {/* Rear window */}
          <path d="M 145 210 L 455 210 L 455 240 Q 455 270 410 270 L 190 270 Q 145 270 145 240 Z" fill="none" stroke="#94a3b8" strokeWidth="2" />
          {/* Roof mid-line */}
          <line x1="145" y1="170" x2="455" y2="170" stroke="#475569" strokeWidth="1" strokeDasharray="4,4" />
          {/* Wheels */}
          <rect x="70" y="65" width="18" height="45" rx="4" fill="#475569" />
          <rect x="70" y="190" width="18" height="45" rx="4" fill="#475569" />
          <rect x="512" y="65" width="18" height="45" rx="4" fill="#475569" />
          <rect x="512" y="190" width="18" height="45" rx="4" fill="#475569" />
          {/* Labels */}
          <text x="300" y="20" textAnchor="middle" fill="#64748b" fontSize="12">FRONT</text>
          <text x="300" y="295" textAnchor="middle" fill="#64748b" fontSize="12">REAR</text>
          <text x="30" y="155" textAnchor="middle" fill="#64748b" fontSize="10" transform="rotate(-90 30 155)">DRIVER</text>
          <text x="570" y="155" textAnchor="middle" fill="#64748b" fontSize="10" transform="rotate(90 570 155)">PASSENGER</text>
        </svg>

        {/* Markers */}
        {(inspection.markers || []).map((m) => {
          const color = (MARKER_TYPES.find((t) => t.key === m.type) || {}).color || "#38bdf8";
          return (
            <div
              key={m.id}
              data-testid={`${testId}-marker-${m.id}`}
              className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ left: `${m.x_pct}%`, top: `${m.y_pct}%` }}
            >
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-lg flex items-center justify-center text-[9px] font-bold text-white"
                style={{ background: color }}
              >
                {(MARKER_TYPES.findIndex((t) => t.key === m.type) + 1)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Marker list w/ optional notes + delete */}
      {markerCount > 0 && (
        <div className="space-y-1.5">
          <div className="label-caps text-[10px] text-white/60">Flagged items · {markerCount}</div>
          {(inspection.markers || []).map((m, i) => {
            const t = MARKER_TYPES.find((x) => x.key === m.type) || MARKER_TYPES[4];
            return (
              <div key={m.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] px-2.5 py-1.5">
                <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
                     style={{ background: t.color }}>{i + 1}</div>
                <span className="text-[11px] text-white/80 w-16 truncate">{t.label}</span>
                <input
                  data-testid={`${testId}-note-${m.id}`}
                  value={m.note}
                  onChange={(e) => setNote(m.id, e.target.value)}
                  placeholder="Optional note (e.g. driver door)"
                  className="flex-1 bg-transparent border-0 border-b border-white/10 focus:border-cyan focus:outline-none text-[11px] text-white px-1 py-0.5"
                />
                <button
                  type="button"
                  data-testid={`${testId}-remove-${m.id}`}
                  onClick={() => removeMarker(m.id)}
                  className="text-white/40 hover:text-red-300 transition"
                  aria-label="Remove marker"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Acknowledgment */}
      <div className={`rounded-lg border p-3 ${
        inspection.acknowledged
          ? "border-lime-400/40 bg-lime-400/[0.06]"
          : "border-amber-400/30 bg-amber-400/[0.05]"
      }`}>
        {inspection.acknowledged ? (
          <div className="flex items-center gap-2 text-[12px] text-lime-200">
            <CheckCircle2 size={14} />
            <span>
              Inspection acknowledged {markerCount === 0 ? "— vehicle reported clean" : `— ${markerCount} item(s) flagged`}.
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-start gap-2 text-[12px] text-amber-200">
              <AlertTriangle size={14} className="mt-0.5" />
              <span>You must acknowledge the vehicle's current condition to continue.</span>
            </div>
            <button
              type="button"
              data-testid={`${testId}-acknowledge`}
              onClick={acknowledge}
              className="px-3 py-1.5 rounded-full text-[11px] label-caps border border-lime-400/50 text-lime-300 hover:bg-lime-400/10 inline-flex items-center gap-1"
            >
              <Check size={12} />
              {markerCount === 0 ? "Confirm the vehicle looks clean" : `Confirm the ${markerCount} flagged item(s)`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
